import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ImageUpload, resolveSrc } from "../../components/ImageUpload";
import {
  eventsApi,
  pubsApi,
  boardApi,
  galleryApi,
  adminGetSubmissions,
  getContent,
  updateContent,
  formatApiErrorDetail,
} from "../../lib/api";
import { GROUPS, DEFAULTS } from "../../data/contentRegistry";
import { useContent } from "../../context/ContentContext";
import {
  PRESETS,
  SCHEMES,
  DEFAULT_PRIMARY,
  DEFAULT_FOOTER,
  DEFAULT_ACCENT,
  PRIMARY_KEY,
  FOOTER_KEY,
  ACCENT_KEY,
  applyTheme,
  setPrimaryVars,
  setFooterVars,
  setAccentVars,
  isValidHex,
  contrastWithWhite,
} from "../../lib/theme";

const CONFIGS = {
  gallery: {
    label: "Gallery",
    api: galleryApi,
    primary: "caption",
    image: "src",
    empty: { src: "", caption: "", order: 0 },
    fields: [
      { key: "src", label: "Image", type: "image" },
      { key: "caption", label: "Caption", type: "text" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  events: {
    label: "Events",
    api: eventsApi,
    primary: "title",
    empty: { category: "upcoming", title: "", date: "", location: "", description: "", order: 0 },
    fields: [
      { key: "category", label: "Category", type: "select", options: ["upcoming", "past"] },
      { key: "title", label: "Title", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  publications: {
    label: "Publications",
    api: pubsApi,
    primary: "title",
    empty: { category: "report", title: "", date: "", excerpt: "", author: "", slug: "", order: 0 },
    fields: [
      { key: "category", label: "Category", type: "select", options: ["report", "article", "research", "newsletter", "resource"] },
      { key: "title", label: "Title", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "author", label: "Author", type: "text" },
      { key: "slug", label: "Article slug (optional)", type: "text" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  board: {
    label: "Board Members",
    api: boardApi,
    primary: "name",
    image: "image",
    empty: { name: "", role: "", image: "", bio: "", order: 0 },
    fields: [
      { key: "image", label: "Portrait", type: "image" },
      { key: "name", label: "Name", type: "text" },
      { key: "role", label: "Role / Title", type: "text" },
      { key: "bio", label: "Biography", type: "textarea" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
};

const inputCls =
  "w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy";

function EntityManager({ cfg }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {} or item
  const [form, setForm] = useState({});

  const load = () => cfg.api.list().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const openNew = () => {
    setForm({ ...cfg.empty });
    setEditing({});
  };
  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
  };
  const close = () => setEditing(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {};
    cfg.fields.forEach((f) => {
      payload[f.key] = f.type === "number" ? Number(form[f.key] || 0) : form[f.key] || "";
    });
    try {
      if (editing && editing.id) {
        await cfg.api.update(editing.id, payload);
        toast.success("Updated");
      } else {
        await cfg.api.create(payload);
        toast.success("Created");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await cfg.api.remove(item.id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`manager-${cfg.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl text-amf-navy">{cfg.label}</h2>
        <button
          onClick={openNew}
          data-testid="admin-add-button"
          className="amf-overline inline-flex items-center gap-2 bg-amf-burgundy text-amf-cream px-4 py-2.5 hover:bg-[var(--amf-accent-dark)] transition-colors"
        >
          <Plus size={15} /> Add New
        </button>
      </div>

      <div className="space-y-px bg-amf-gold/25 border border-amf-gold/25">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid="admin-item-row"
            className="bg-white p-4 flex items-center gap-4"
          >
            {cfg.image && (
              <img
                src={resolveSrc(item[cfg.image])}
                alt=""
                className="w-12 h-12 object-cover border border-amf-gold/30 shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-playfair text-lg text-amf-navy truncate">{item[cfg.primary]}</p>
              {item.category && <span className="amf-overline text-amf-stone">{item.category}</span>}
            </div>
            <button onClick={() => openEdit(item)} data-testid="admin-edit-button" className="text-amf-navy hover:text-amf-burgundy p-2">
              <Pencil size={17} />
            </button>
            <button onClick={() => remove(item)} data-testid="admin-delete-button" className="text-amf-stone hover:text-amf-burgundy p-2">
              <Trash2 size={17} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="bg-white p-8 text-center font-lora text-amf-stone">No items yet.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-amf-navy/60 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-amf-cream w-full max-w-2xl p-8" data-testid="admin-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-playfair text-2xl text-amf-navy">
                {editing.id ? "Edit" : "New"} {cfg.label}
              </h3>
              <button onClick={close} className="text-amf-stone hover:text-amf-burgundy">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={save} className="space-y-5" data-testid="admin-entity-form">
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <label className="amf-overline text-amf-stone block mb-2">{f.label}</label>
                  {f.type === "image" ? (
                    <ImageUpload value={form[f.key]} onChange={(v) => setField(f.key, v)} />
                  ) : f.type === "textarea" ? (
                    <textarea
                      rows={5}
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={`${inputCls} resize-none`}
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={inputCls}
                    >
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={inputCls}
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid="admin-save-button"
                  className="amf-overline bg-amf-burgundy text-amf-cream px-6 py-3 hover:bg-[var(--amf-accent-dark)] transition-colors"
                >
                  Save
                </button>
                <button type="button" onClick={close} className="amf-overline border border-amf-navy text-amf-navy px-6 py-3 hover:bg-amf-navy hover:text-amf-cream transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Submissions() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    adminGetSubmissions().then(setSubs).catch(() => setSubs([]));
  }, []);
  return (
    <div data-testid="manager-submissions">
      <h2 className="font-playfair text-2xl text-amf-navy mb-6">Submissions</h2>
      <div className="space-y-px bg-amf-gold/25 border border-amf-gold/25">
        {subs.map((s) => (
          <div key={s.id} className="bg-white p-5" data-testid="submission-row">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <span className="amf-overline text-amf-burgundy">{s.type}</span>
              <span className="font-playfair text-lg text-amf-navy">{s.name}</span>
              <a href={`mailto:${s.email}`} className="font-lora text-sm text-amf-stone hover:text-amf-burgundy">{s.email}</a>
            </div>
            {s.subject && <p className="font-inter text-sm text-amf-charcoal/70">Re: {s.subject}</p>}
            <p className="font-lora text-amf-charcoal/85 mt-2">{s.message}</p>
            <p className="amf-overline text-amf-stone mt-2 text-[0.65rem]">{new Date(s.created_at).toLocaleString()}</p>
          </div>
        ))}
        {subs.length === 0 && (
          <div className="bg-white p-8 text-center font-lora text-amf-stone">No submissions yet.</div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "content", label: "Page Content" },
  { key: "appearance", label: "Appearance" },
  { key: "gallery", label: "Gallery" },
  { key: "events", label: "Events" },
  { key: "publications", label: "Publications" },
  { key: "board", label: "Board" },
  { key: "submissions", label: "Submissions" },
];

function ColorControl({ label, hint, value, onChange }) {
  return (
    <div className="mb-10">
      <label className="amf-overline text-amf-navy block mb-1">{label}</label>
      <p className="font-lora text-sm text-amf-stone mb-3">{hint}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl mb-4">
        {PRESETS.map((p) => {
          const selected = value.toLowerCase() === p.hex.toLowerCase();
          return (
            <button
              key={p.hex}
              type="button"
              onClick={() => onChange(p.hex)}
              data-testid={`theme-${label.split(" ")[0].toLowerCase()}-${p.hex.replace("#", "")}`}
              className={`flex items-center gap-3 border px-3 py-2.5 text-left transition-colors ${
                selected
                  ? "border-amf-navy ring-1 ring-amf-navy"
                  : "border-amf-navy/15 hover:border-amf-navy/40"
              }`}
            >
              <span
                className="h-7 w-7 rounded-full border border-black/10 shrink-0"
                style={{ background: p.hex }}
              />
              <span className="block font-lora text-sm text-amf-charcoal">{p.name}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="color"
          value={isValidHex(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-14 p-1 border border-amf-navy/15 bg-white cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#173F7A"
          className="w-40 bg-white border border-amf-navy/15 px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy"
        />
      </div>
      {isValidHex(value) && contrastWithWhite(value) < 3 && (
        <p className="font-lora text-sm text-amf-burgundy mt-3">
          ⚠ This color is light — white text on it may be hard to read. Consider
          a deeper shade for good contrast.
        </p>
      )}
    </div>
  );
}

function ThemeManager() {
  const { refresh } = useContent();
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [footer, setFooter] = useState(DEFAULT_FOOTER);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getContent()
      .then((d) => {
        setPrimary(d[PRIMARY_KEY] || DEFAULT_PRIMARY);
        setFooter(d[FOOTER_KEY] || DEFAULT_FOOTER);
        setAccent(d[ACCENT_KEY] || DEFAULT_ACCENT);
      })
      .catch(() => {});
  }, []);

  // Live preview (updates CSS vars immediately; persisted only on Save).
  const onPrimary = (hex) => {
    setPrimary(hex);
    if (isValidHex(hex)) setPrimaryVars(hex);
  };
  const onFooter = (hex) => {
    setFooter(hex);
    if (isValidHex(hex)) setFooterVars(hex);
  };
  const onAccent = (hex) => {
    setAccent(hex);
    if (isValidHex(hex)) setAccentVars(hex);
  };
  const applyScheme = (s) => {
    onPrimary(s.primary);
    onAccent(s.accent);
  };

  const save = async () => {
    if (!isValidHex(primary) || !isValidHex(footer) || !isValidHex(accent)) {
      toast.error("Enter valid hex colors, e.g. #173F7A");
      return;
    }
    setSaving(true);
    try {
      const updates = {};
      const deletes = [];
      if (primary.toLowerCase() === DEFAULT_PRIMARY.toLowerCase()) deletes.push(PRIMARY_KEY);
      else updates[PRIMARY_KEY] = primary;
      if (footer.toLowerCase() === DEFAULT_FOOTER.toLowerCase()) deletes.push(FOOTER_KEY);
      else updates[FOOTER_KEY] = footer;
      if (accent.toLowerCase() === DEFAULT_ACCENT.toLowerCase()) deletes.push(ACCENT_KEY);
      else updates[ACCENT_KEY] = accent;

      await updateContent(updates, deletes);
      applyTheme({ primary, footer, accent });
      refresh?.();
      toast.success("Theme saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    onPrimary(DEFAULT_PRIMARY);
    onFooter(DEFAULT_FOOTER);
    onAccent(DEFAULT_ACCENT);
  };

  const isDefault =
    primary.toLowerCase() === DEFAULT_PRIMARY.toLowerCase() &&
    footer.toLowerCase() === DEFAULT_FOOTER.toLowerCase() &&
    accent.toLowerCase() === DEFAULT_ACCENT.toLowerCase();

  return (
    <div data-testid="manager-appearance">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-playfair text-2xl text-amf-navy">Appearance</h2>
        <button
          onClick={save}
          disabled={saving}
          data-testid="theme-save-button"
          className="amf-overline bg-amf-burgundy text-amf-cream px-5 py-2.5 hover:bg-[var(--amf-accent-dark)] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Theme"}
        </button>
      </div>

      <p className="font-lora text-amf-stone mb-8 max-w-2xl">
        Three colors define the whole site. <strong>Primary</strong> covers the
        header, hero, section headings and navigation; <strong>Footer</strong>{" "}
        covers just the site footer background (defaults to match Primary, but
        can be set independently); <strong>Accent</strong> covers buttons,
        links and highlights. Changing a color updates <em>every</em> element
        using it. Changes preview here instantly — click <em>Save Theme</em> to
        apply them across the live site.
      </p>

      <label className="amf-overline text-amf-navy block mb-3">Quick schemes</label>
      <div className="flex flex-wrap gap-2.5 mb-10">
        {SCHEMES.map((s) => {
          const active =
            primary.toLowerCase() === s.primary.toLowerCase() &&
            accent.toLowerCase() === s.accent.toLowerCase();
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => applyScheme(s)}
              data-testid={`theme-scheme-${s.name.split(" ")[0].toLowerCase()}`}
              className={`flex items-center gap-2 border px-3 py-2 transition-colors ${
                active
                  ? "border-amf-navy ring-1 ring-amf-navy"
                  : "border-amf-navy/15 hover:border-amf-navy/40"
              }`}
            >
              <span className="flex">
                <span className="h-5 w-5 rounded-l-full border border-black/10" style={{ background: s.primary }} />
                <span className="h-5 w-5 rounded-r-full border border-l-0 border-black/10" style={{ background: s.accent }} />
              </span>
              <span className="font-lora text-sm text-amf-charcoal">{s.name}</span>
            </button>
          );
        })}
      </div>

      <ColorControl
        label="Primary color"
        hint="Header, hero, section headings, navigation — the deep blue areas."
        value={primary}
        onChange={onPrimary}
      />
      <ColorControl
        label="Footer color"
        hint="The site footer background only. Set this independently if you want the footer a different shade from the header/hero."
        value={footer}
        onChange={onFooter}
      />
      <ColorControl
        label="Accent color"
        hint="Buttons, links, active states and highlights."
        value={accent}
        onChange={onAccent}
      />

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <span className="amf-overline text-amf-stone mr-1">Live preview:</span>
        <span className="amf-overline bg-amf-navy text-amf-cream px-3 py-1.5">Header</span>
        <span className="amf-overline bg-amf-footer text-amf-cream px-3 py-1.5">Footer</span>
        <span className="amf-overline bg-amf-burgundy text-amf-cream px-3 py-1.5">Button</span>
        <span className="amf-overline text-amf-burgundy border border-amf-burgundy px-3 py-1.5">Link</span>
      </div>

      {!isDefault && (
        <button
          onClick={resetAll}
          data-testid="theme-revert"
          className="amf-overline text-amf-stone underline hover:text-amf-navy"
        >
          Reset to original colors
        </button>
      )}
    </div>
  );
}

function ContentManager() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(GROUPS[0].group);

  useEffect(() => {
    getContent()
      .then((data) => {
        const merged = { ...DEFAULTS, ...data };
        setValues(merged);
      })
      .catch(() => setValues({ ...DEFAULTS }));
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Send only non-default keys as updates; revert default-valued keys via deletes
      const changed = {};
      const deletes = [];
      Object.keys(values).forEach((k) => {
        if (values[k] !== DEFAULTS[k]) changed[k] = values[k];
        else deletes.push(k);
      });
      await updateContent(changed, deletes);
      toast.success("Page content saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const group = GROUPS.find((g) => g.group === active);

  return (
    <div data-testid="manager-content">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-playfair text-2xl text-amf-navy">Page Content</h2>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="amf-overline bg-amf-burgundy text-amf-cream px-5 py-2.5 hover:bg-[var(--amf-accent-dark)] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {GROUPS.map((g) => (
          <button
            key={g.group}
            onClick={() => setActive(g.group)}
            data-testid={`content-group-${g.group.toLowerCase().replace(/\s+/g, "-")}`}
            className={`amf-overline px-4 py-2 border transition-colors ${
              active === g.group
                ? "bg-amf-navy text-amf-cream border-amf-navy"
                : "border-amf-navy/20 text-amf-stone hover:border-amf-navy"
            }`}
          >
            {g.group}
          </button>
        ))}
      </div>

      <div className="space-y-6 max-w-3xl">
        {group.fields.map((f) => (
          <div key={f.key}>
            <label className="amf-overline text-amf-stone block mb-2">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea
                data-testid={`content-field-${f.key}`}
                rows={f.default.length > 160 ? 5 : 2}
                value={values[f.key] ?? ""}
                onChange={(e) => setVal(f.key, e.target.value)}
                className="w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy resize-none"
              />
            ) : (
              <input
                data-testid={`content-field-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setVal(f.key, e.target.value)}
                className="w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("gallery");

  return (
    <div className="min-h-screen bg-amf-cream" data-testid="admin-page">
      <header className="bg-amf-navy text-amf-cream">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/board/logo-circle.png" alt="AMF" className="h-9 w-9 rounded-full" />
            <span className="font-playfair text-lg">AMF Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="amf-overline text-amf-cream/70 hidden sm:inline">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="amf-overline inline-flex items-center gap-2 hover:text-amf-gold transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-x-7 gap-y-2 border-b border-amf-gold/30 pb-4 mb-10">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`amf-overline pb-1 border-b-2 -mb-[18px] transition-colors ${
                tab === t.key
                  ? "text-amf-burgundy border-amf-burgundy"
                  : "text-amf-stone border-transparent hover:text-amf-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "submissions" ? (
          <Submissions />
        ) : tab === "content" ? (
          <ContentManager />
        ) : tab === "appearance" ? (
          <ThemeManager />
        ) : (
          <EntityManager cfg={CONFIGS[tab]} key={tab} />
        )}
      </div>
    </div>
  );
}
